/**
 * Single source of truth mapping route paths to human-readable page titles.
 *
 * Used to set {@code document.title} and to announce navigations to screen
 * readers on client-side route changes (SPAs do neither by default — see
 * WCAG 2.4.2 "Page Titled" and the SPA route-announcement guidance).
 *
 * Resolution order: exact path match first, then the longest matching prefix
 * (so {@code /po/create} wins over the {@code /po/} detail prefix), then a
 * sensible fallback.
 */
export const APP_NAME = "Eclipse"

/** Fixed routes — exact path → title. */
const EXACT_TITLES: Readonly<Record<string, string>> = {
  "/": "Dashboard",
  "/callback": "Signing in",
  // Purchasing
  "/po": "Purchase Orders",
  "/po/create": "Create Purchase Order",
  "/suppliers": "Suppliers",
  "/invoices": "Invoices",
  "/exceptions": "PO Exceptions",
  // Items & Recipes
  "/items/ingredients": "Ingredients",
  "/items/recipes": "Recipes",
  // Inventory
  "/inventory": "Inventory",
  "/inventory/move": "Move Stock",
  "/locations": "Locations",
  "/reorder-alerts": "Reorder Alerts",
  "/reorder-config": "Reorder Config",
  "/pick-recommendation": "Pick Recommendation",
  "/quality-hold": "Quality Hold",
  "/trace": "Traceability",
  // Manufacturing
  "/work-orders": "Work Orders",
  "/work-orders/create": "Create Work Order",
  "/work-order-plans": "Multi-Level Planning",
  // Planning
  "/mrp": "MRP Planning",
  "/production-plan": "MRP Planning",
  // Fulfillment
  "/store-orders": "Store Orders",
  "/pick-tickets": "Pick Tickets",
  "/delivery-schedule": "Delivery Schedule",
  "/shipping": "Shipping",
  // Analytics
  "/supplier-metrics": "Supplier Metrics",
  "/accounting": "Cost Summary",
  // Print
  "/print-center": "Print Center",
}

/** Parametric routes — path prefix → title (matched longest-first). */
const PREFIX_TITLES: ReadonlyArray<readonly [string, string]> = [
  ["/po/", "Purchase Order"],
  ["/suppliers/", "Supplier"],
  ["/recipes/", "Recipe"],
  ["/items/", "Item"],
  ["/work-orders/", "Work Order"],
] as const

/** The fallback title when no route rule matches. */
const FALLBACK_TITLE = "Eclipse"

/**
 * Resolves the human-readable page title for a route path.
 */
export function resolvePageTitle(pathname: string): string {
  const exact = EXACT_TITLES[pathname]
  if (exact) {
    return exact
  }
  const prefixMatch = PREFIX_TITLES.filter(([prefix]) => pathname.startsWith(prefix)).sort(
    (a, b) => b[0].length - a[0].length,
  )[0]
  return prefixMatch ? prefixMatch[1] : FALLBACK_TITLE
}
